import {
  configureStore, createListenerMiddleware,
  ActionCreator, EnhancedStore,
  ListenerEffectAPI, ListenerErrorHandler,
  UnknownAction, ThunkDispatch, Dispatch
} from '@reduxjs/toolkit';

import { reducer } from '@/redux/slices';
import { addListeners } from '@/redux/epics';
import { AjaxService, ApiServices, LoggerService } from '@/services';
import { createLogger } from 'redux-logger';
import { GetServerSidePropsContext } from 'next';

import { isSSR } from '@/utils/millesimaUtils';
import { ConfigurationService } from '@/services/Configuration.service';
import { NewsletterV2Service } from '@/services/NewsletterV2Service';
import { ShippingGateway } from '@/redux/thunks/ShippingGateway';
import { BlogService } from '@/services/Blog.service';
import { CheckoutServiceV2 } from '@/services/CheckoutServiceV2';
import HipayService from '@/services/Hipay.service';
import { CheckoutServiceInterface } from '@/services/CheckoutService.interface';
import { PaymentProvidersStrategy } from '@/services/PaymentProviders.strategy';
import UserService from '@/services/User.service';
import { HttpShippingGateway } from '@/redux/thunks/HttpShippingGateway';
import { CartGateway } from '@/services/cart/cart.gateway';
import { HttpCartGateway } from '@/services/cart/HttpCart.gateway';
import DatalayerService from '@/services/Datalayer.service';

export type RootState = ReturnType<typeof reducer>;
export type Dependencies = {
  shippingGateway: ShippingGateway;
  configurationService: ConfigurationService;
  newsletterService: NewsletterV2Service;
  blogMenuService: BlogService;
  checkoutService: CheckoutServiceInterface;
  hipayService: HipayService;
  userService: UserService;
  loggerService: LoggerService;
  cartGateway: CartGateway;
  datalayerService: DatalayerService;
};

export interface CustomStore extends EnhancedStore {
  startAppListening: ReturnType<typeof createListenerMiddleware>['startListening'];
  ajaxService: AjaxService;
  services: ApiServices;
  debugId: number;
}

export const createStore = (preloadedState?: Partial<RootState>, ssrContext?: GetServerSidePropsContext) => {
  const isReduxLogger = process.env.NEXT_PUBLIC_REDUX_LOGGER === 'true';
  let loggerMiddleware: any = undefined;

  if (!isSSR() && isReduxLogger) {
    loggerMiddleware = createLogger({
      level: 'warn',
      collapsed: true
    });
  }

  const ajaxService = new AjaxService();
  if (ssrContext) {
    ajaxService.saveRequestContext(ssrContext);
  }

  const services = new ApiServices(ajaxService);

  const listenerMiddleware = createListenerMiddleware({
    extra: services,
  });
  const paymentProvidersStrategy = new PaymentProvidersStrategy(ajaxService);

  // const shippingGateway = new FakeShippingGateway();
  // shippingGateway.availableTypes = ['L', 'PA', 'P'];

  const dependencies = {
    configurationService: new ConfigurationService(),
    newsletterService: new NewsletterV2Service(ajaxService),
    blogMenuService: new BlogService(ajaxService),
    checkoutService: new CheckoutServiceV2(paymentProvidersStrategy, ajaxService),
    hipayService: new HipayService(ajaxService),
    userService: services.userService,
    shippingGateway: new HttpShippingGateway(ajaxService),
    loggerService: new LoggerService(),
    cartGateway: new HttpCartGateway(ajaxService),
    datalayerService: new DatalayerService(),
  } satisfies Dependencies;

  const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
      thunk: {
        extraArgument: dependencies
      },
      serializableCheck: false,
    }).prepend(listenerMiddleware.middleware)
      .concat(
        [loggerMiddleware].filter(Boolean)
      ),
    preloadedState
  }) as CustomStore;

  store.startAppListening = listenerMiddleware.startListening;
  store.ajaxService = ajaxService;
  store.services = services;
  paymentProvidersStrategy.dispatch = store.dispatch;

  addListeners(store);

  return store;
};

export type AppStore = ReturnType<typeof createStore> & {
  dispatch: AppDispatch;
};
export type AppState = ReturnType<AppStore['getState']>;
export type AppDispatch = Dispatch<UnknownAction> & ThunkDispatch<AppState, Dependencies, UnknownAction>;

export type AppListenerEffectAPI = ListenerEffectAPI<AppState, AppDispatch, ApiServices>

export type AppListenerEntry = {
  actionCreator?: ActionCreator<UnknownAction>;
  matcher?: (action: UnknownAction) => boolean;
  effect: (action: ReturnType<any>, api: AppListenerEffectAPI) => void;
}

const _listenerErrorHandler: ListenerErrorHandler = (error: unknown, errorInfo): void => {
  console.log('RTK ERROR HANDLER', error, errorInfo);
};
